<template>
    <div class="relative h-full flex items-center gap-2">
        <BreadcrumbWrapper>
            <BreadcrumbLoading v-if="isLoading" />

            <template v-else>
                <!-- Company -->
                <BreadcrumbNavItem v-if="canViewCompany" ref="companyNavItemRef">
                    <BreadcrumbLink
                        v-if="company"
                        label="Company"
                        :name="company.name ?? 'Untitled'"
                        :to="companyRoutePreserving(company)"
                        :active="activeLevel === 'company'"
                        :switchable="canSwitchCompany"
                        @toggle-dropdown="() => openDropdown('company')"
                    />
                    <BreadcrumbPlaceholder
                        v-else
                        :count="companies.length"
                        label="Company"
                        @click="() => openDropdown('company')"
                    />
                </BreadcrumbNavItem>

                <!-- Portfolio -->
                <BreadcrumbNavItem v-if="showPortfolioLevel" ref="portfolioNavItemRef">
                    <BreadcrumbSeparator v-if="canViewCompany" />
                    <BreadcrumbLink
                        v-if="portfolio"
                        label="Portfolio"
                        :name="portfolio.name ?? 'Untitled'"
                        :to="isCurrentOnAssetPage ? portfolioListRoute(portfolio) : portfolioRoute(portfolio)"
                        :active="activeLevel === 'portfolio'"
                        @toggle-dropdown="() => openDropdown('portfolio')"
                    />
                    <BreadcrumbPlaceholder
                        v-else
                        :count="company?.portfolios.length || 0"
                        label="Portfolio"
                        @click="() => openDropdown('portfolio')"
                    />
                </BreadcrumbNavItem>

                <!-- Asset -->
                <BreadcrumbNavItem v-if="showAssetLevel" ref="assetNavItemRef">
                    <BreadcrumbSeparator />
                    <BreadcrumbLink
                        v-if="asset"
                        label="Asset"
                        :name="asset.name ?? 'Untitled'"
                        :to="assetRoute(asset)"
                        :active="activeLevel === 'asset'"
                        @toggle-dropdown="() => openDropdown('asset')"
                    />
                    <BreadcrumbPlaceholder
                        v-else
                        :count="portfolio?.assets.length || 0"
                        label="Asset"
                        @click="() => openDropdown(portfolio ? 'asset' : 'portfolio')"
                    />
                </BreadcrumbNavItem>

                <!-- Error state -->
                <li v-if="error" class="text-text-secondary paragraph-md">
                    Error loading navigation
                </li>
            </template>
        </BreadcrumbWrapper>

        <!-- Company Dropdown -->
        <DropdownPosition
            v-if="isCompanyDropdownOpen && companyNavItemRef?.itemRef"
            :is-open="isCompanyDropdownOpen"
            :anchor-ref="companyNavItemRef.itemRef"
            alignment="left"
            @close="closeAllDropdowns"
        >
            <CompanyDropdown
                v-model:is-open="isCompanyDropdownOpen"
            />
        </DropdownPosition>

        <!-- Portfolio/Asset Dropdown -->
        <DropdownPosition
            v-if="isPortfolioDropdownOpen && portfolioNavItemRef?.itemRef"
            :is-open="isPortfolioDropdownOpen"
            :anchor-ref="portfolioNavItemRef.itemRef"
            alignment="left"
            @close="closeAllDropdowns"
        >
            <PortfolioDropdown
                v-model:is-open="isPortfolioDropdownOpen"
            />
        </DropdownPosition>

        <DropdownPosition
            v-if="isPortfolioAssetDropdownOpen && portfolioAssetAnchorRef"
            :is-open="isPortfolioAssetDropdownOpen"
            :anchor-ref="portfolioAssetAnchorRef"
            :alignment="portfolioAssetTriggerSource === 'portfolio' ? 'left' : 'middle'"
            @close="closeAllDropdowns"
        >
            <PortfolioAssetDropdown
                v-model:is-open="isPortfolioAssetDropdownOpen"
                :trigger-source="portfolioAssetTriggerSource"
            />
        </DropdownPosition>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'
import { useRoute } from 'vue-router'
import { PERMISSION_READ_PORTFOLIOS } from '@/vue/helpers/permissions'
import { useUserStore } from '@/vue/stores/userStore'
import BreadcrumbLink from './breadcrumb/breadcrumb-link.vue'
import BreadcrumbLoading from './breadcrumb/breadcrumb-loading.vue'
import BreadcrumbNavItem from './breadcrumb/breadcrumb-nav-item.vue'
import BreadcrumbPlaceholder from './breadcrumb/breadcrumb-placeholder.vue'
import BreadcrumbSeparator from './breadcrumb/breadcrumb-separator.vue'
import BreadcrumbWrapper from './breadcrumb/breadcrumb-wrapper.vue'
import CompanyDropdown from './dropdown/company-dropdown.vue'
import PortfolioAssetDropdown from './dropdown/portfolio-asset-dropdown.vue'
import PortfolioDropdown from './dropdown/portfolio-dropdown.vue'
import DropdownPosition from './dropdown/ui/dropdown-position.vue'
import { useBreadcrumb_experimental } from './use-breadcrumbs'
import { useCompanyList } from './use-company-list'
import { useNavigationRoutes } from './use-navigation-routes'

const props = withDefaults(defineProps<{
    levelDepth?: 'company' | 'portfolio' | 'asset'
}>(), {
    levelDepth: 'asset',
})

type DropdownType = 'company' | 'portfolio' | 'asset'

// Template refs for dropdown positioning
const companyNavItemRef = useTemplateRef<InstanceType<typeof BreadcrumbNavItem>>('companyNavItemRef')
const portfolioNavItemRef = useTemplateRef<InstanceType<typeof BreadcrumbNavItem>>('portfolioNavItemRef')
const assetNavItemRef = useTemplateRef<InstanceType<typeof BreadcrumbNavItem>>('assetNavItemRef')

const { company, portfolio, asset, isLoading, error } = useBreadcrumb_experimental()
const { companies } = useCompanyList()
const { companyRoutePreserving, portfolioRoute, portfolioListRoute, assetRoute } = useNavigationRoutes()
const route = useRoute()
const userStore = useUserStore()

const isCurrentOnAssetPage = computed(() => route.name?.toString().startsWith('asset.'))

// Permission checks
const canViewCompany = computed(() =>
    userStore.hasAnyCompanyAccess
    || userStore.hasPermission(PERMISSION_READ_PORTFOLIOS),
)

const canSwitchCompany = computed(() =>
    // Show company dropdown if user has access to more than one company
    companies.value.length > 1,
)

const showPortfolioLevel = computed(() => {
    if (props.levelDepth === 'company') {
        return false
    }
    // If user can't view company level, show portfolio level
    if (!canViewCompany.value) {
        return true
    }
    // otherwise show portfolio level if company is selected
    return !!company.value
})

const showAssetLevel = computed(() => {
    return showPortfolioLevel.value && props.levelDepth === 'asset'
})

const activeLevel = computed<'company' | 'portfolio' | 'asset'>(() => {
    if (asset.value) return 'asset'
    if (portfolio.value) return 'portfolio'
    return 'company'
})

// Dropdown state
const isCompanyDropdownOpen = ref(false)
const isPortfolioDropdownOpen = ref(false)
const isPortfolioAssetDropdownOpen = ref(false)
const portfolioAssetTriggerSource = ref<'portfolio' | 'asset' | null>(null)

const portfolioAssetAnchorRef = computed(() =>
    portfolioAssetTriggerSource.value === 'portfolio'
        ? portfolioNavItemRef.value?.itemRef
        : assetNavItemRef.value?.itemRef,
)

function openDropdown(type: DropdownType) {
    isCompanyDropdownOpen.value = false
    isPortfolioDropdownOpen.value = false
    isPortfolioAssetDropdownOpen.value = false
    portfolioAssetTriggerSource.value = null

    if (type === 'company') {
        isCompanyDropdownOpen.value = true
        return
    }

    if (type === 'portfolio') {
        if (props.levelDepth === 'asset') {
            isPortfolioAssetDropdownOpen.value = true
            portfolioAssetTriggerSource.value = 'portfolio'
            return
        }
        isPortfolioDropdownOpen.value = true
        return
    }

    isPortfolioAssetDropdownOpen.value = true
    portfolioAssetTriggerSource.value = type
}

function closeAllDropdowns() {
    isCompanyDropdownOpen.value = false
    isPortfolioDropdownOpen.value = false
    isPortfolioAssetDropdownOpen.value = false
    portfolioAssetTriggerSource.value = null
}
</script>
